(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.GiorgioSuitSelector = api;
})(typeof window === 'undefined' ? this : window, function () {
  const drops = [10, 8, 6, 4];
  const number = value => /^\d+$/.test(String(value || '')) ? Number(value) : null;
  function pantChoices(chest, pairs) {
    const jacket = number(chest);
    if (jacket === null) return [];
    const actual = new Set((pairs || []).filter(pair => String(pair[0]) === String(chest)).map(pair => String(pair[1])));
    const waists = [...new Set((pairs || []).map(pair => String(pair[1])))].sort((a, b) => Number(a) - Number(b));
    return waists.map(waist => {
      const compatible = actual.has(waist), drop = jacket - Number(waist);
      return {waist, compatible, recommended: drops.includes(drop), standard: drop === 6};
    });
  }
  // Only real catalog pairings (the jacket/pant variants that exist) can be chosen. There is no custom sizing.
  function planPant({chest, waist, explicit = false, pairs}) {
    const choices = pantChoices(chest, pairs);
    const listed = choices.some(choice => choice.waist === String(waist) && choice.compatible);
    if (explicit) return {waist: listed ? String(waist) : '', choices, changed: Boolean(waist) && !listed, explicit: true};
    const recommended = choices.find(choice => choice.standard && choice.compatible);
    return {waist: recommended?.waist || '', choices, changed: String(waist || '') !== (recommended?.waist || ''), explicit: false};
  }
  function fulfillmentPlan(chest, waist, pairs) {
    const jacket = number(chest), requested = number(waist);
    if (jacket === null || requested === null) return null;
    const exact = (pairs || []).some(pair => String(pair[0]) === String(chest) && String(pair[1]) === String(waist));
    if (!exact) return null;
    return {chest: String(chest), waist: String(waist), referenceWaist: String(waist), custom: false};
  }
  function eligible(type, names, pairs) {
    return type === 'Suit' && names.length === 2 && names.includes('Jacket Size') && names.includes('Pant Size') && Array.isArray(pairs) && pairs.length > 0 && pairs.every(pair => pair.length === 2 && pair.every(value => number(value) !== null));
  }
  class SuitSelector {
    constructor(product) {
      this.product = product;
      this.form = product.form;
      this.document = product.ownerDocument;
      this.window = this.document.defaultView;
      this.config = product.state.suitSelector;
      this.labels = this.config.labels;
      this.jacket = product.optionGroups.find(group => group.dataset.optionName === 'Jacket Size');
      this.pant = product.optionGroups.find(group => group.dataset.optionName === 'Pant Size');
      const jacketIndex = product.optionGroups.indexOf(this.jacket);
      const pantIndex = product.optionGroups.indexOf(this.pant);
      this.pairs = this.config.pairs.map(pair => [pair[jacketIndex], pair[pantIndex]]);
      this.explicitPant = Boolean(product.state.selectionExplicit && this.selected(this.pant));
      // Desktop shows fit, length and sizes inline; the drawers are used on mobile only.
      this.desktopQuery = this.window.matchMedia('(min-width: 990px)');
      this.onLayoutChange = () => this.layout();
      this.observer = new MutationObserver(() => {
        if (this.enhanced) { this.refresh(); return; }
        this.enhance();
        if (this.enhanced) this.product.updateControls();
      });
      this.observer.observe(product.appOptions, {subtree: true, childList: true});
      this.enhance();
    }
    selected(group) { return group?.querySelector('input:checked')?.value || ''; }
    fields(name) { return [...this.product.appOptions.querySelectorAll('input,select,textarea')].filter(field => field.name === `properties[${name}]`); }
    property(name) {
      const fields = this.fields(name);
      const selected = fields.find(field => field.type === 'radio' || field.type === 'checkbox' ? field.checked : field.value.trim());
      return selected?.value?.trim() || '';
    }
    fitReady() { return this.fields('Fit').length > 0 && this.fields('Jacket Length').length > 0; }
    setText(element, value) {
      const text = String(value ?? '');
      if (element.textContent !== text) element.textContent = text;
    }
    element(tag, className, text) {
      const element = this.document.createElement(tag);
      if (className) element.className = className;
      if (text) element.textContent = text;
      return element;
    }
    enhance() {
      if (this.enhanced || !this.fitReady()) return;
      const id = `GiorgioSuit-${this.product.dataset.sectionId}`;
      this.controls = this.element('div', 'g-suit-choices');
      this.status = this.element('p', 'g-suit-announcement');
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.status.setAttribute('aria-atomic', 'true');
      this.buttons = {};
      this.dialogs = {};
      for (const kind of ['jacket', 'pant']) {
        const button = this.element('button', 'g-suit-choice');
        button.type = 'button';
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-controls', `${id}-${kind}`);
        const title = this.element('span', 'g-suit-choice-label', this.labels[kind]);
        const summary = this.element('span', 'g-suit-choice-summary');
        button.append(title, summary, this.element('span', 'g-suit-choice-arrow', '+'));
        button.lastElementChild.setAttribute('aria-hidden', 'true');
        this.controls.append(button);
        this.buttons[kind] = {button, summary};
        const dialog = this.element('dialog', 'g-suit-dialog');
        dialog.dataset.gSuitKind = kind;
        dialog.id = `${id}-${kind}`;
        dialog.setAttribute('aria-labelledby', `${dialog.id}-title`);
        const head = this.element('div', 'g-suit-dialog-head');
        const heading = this.element('h2', '', this.labels[`${kind}Title`]);
        heading.id = `${dialog.id}-title`;
        const close = this.element('button', 'g-suit-close', '×');
        close.type = 'button';
        close.setAttribute('aria-label', this.labels.close);
        head.append(heading, close);
        const body = this.element('div', 'g-suit-dialog-body');
        const help = this.element('p', 'g-suit-help', this.labels[`${kind}Help`]);
        help.id = `${dialog.id}-help`;
        const guidance = this.element('p', 'g-suit-guidance');
        guidance.id = `${dialog.id}-guidance`;
        guidance.setAttribute('role', 'status');
        guidance.setAttribute('aria-live', 'polite');
        guidance.setAttribute('aria-atomic', 'true');
        dialog.setAttribute('aria-describedby', `${help.id} ${guidance.id}`);
        const fitLink = this.fitGuideButton();
        body.append(help, fitLink, guidance);
        if (kind === 'pant') {
          const contact = this.element('a', 'g-suit-help-link g-suit-contact-link', this.labels.pantContact);
          const contactUrl = new URL(this.config.contactUrl || '/pages/contact-us', this.window.location.origin);
          if (contactUrl.origin === this.window.location.origin && this.document.body.dataset.gThemeId) contactUrl.searchParams.set('preview_theme_id', this.document.body.dataset.gThemeId);
          contact.href = contactUrl.href;
          const fitNote = this.element('p', 'g-suit-fit-note');
          this.drawerFitNote = fitNote;
          body.append(fitNote, contact);
        }
        const availability = this.element('p', 'g-suit-availability');
        availability.setAttribute('role', 'status');
        availability.setAttribute('aria-live', 'polite');
        availability.setAttribute('aria-atomic', 'true');
        const foot = this.element('div', 'g-suit-dialog-foot');
        const cancel = this.element('button', 'g-suit-cancel', this.labels.cancel);
        cancel.type = 'button';
        const confirm = this.element('button', 'g-button g-suit-confirm', this.labels[`confirm${kind === 'jacket' ? 'Jacket' : 'Pant'}`]);
        confirm.type = 'button';
        foot.append(cancel, confirm);
        dialog.append(head, body, availability, foot);
        this.dialogs[kind] = {dialog, body, confirm, availability, guidance};
        this.form.append(dialog);
        button.addEventListener('click', () => this.open(kind, button));
        for (const control of [close, cancel]) control.addEventListener('click', () => this.close(dialog, false));
        confirm.addEventListener('click', () => { if (this.ready(kind)) this.close(dialog, true); });
        dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(dialog, false); });
        dialog.addEventListener('click', event => {
          if (event.target !== dialog) return;
          const box = dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.close(dialog, false);
        });
        dialog.addEventListener('close', () => {
          if (!dialog.gSuitCommitted && dialog.gSuitSnapshot) this.restore(dialog.gSuitSnapshot);
          dialog.gSuitSnapshot = null;
          this.setText(guidance, '');
          button.setAttribute('aria-expanded', 'false');
          dialog.gSuitOpener?.focus();
          this.refresh();
          this.product.updateSticky();
        });
      }
      this.jacketHome = this.document.createComment('');
      this.pantHome = this.document.createComment('');
      this.appHome = this.document.createComment('');
      this.jacket.before(this.jacketHome);
      this.pant.before(this.pantHome);
      this.product.appOptions.before(this.appHome);
      this.buildInline(id);
      this.form.querySelector('.g-product-options').before(this.controls, this.inline, this.status);
      this.layout();
      this.desktopQuery.addEventListener('change', this.onLayoutChange);
      this.jacket.querySelector('legend').firstChild.textContent = this.labels.chestStep + ' ';
      this.pant.querySelector('legend').firstChild.textContent = this.labels.waistStep + ' ';
      this.onCapture = event => this.change(event);
      this.onInputCapture = event => this.markVariantChange(event);
      this.product.addEventListener('input', this.onInputCapture, true);
      this.product.addEventListener('change', this.onCapture, true);
      this.product.dataset.gSuitEnhanced = 'true';
      this.enhanced = true;
      this.refresh();
    }
    fitGuideButton() {
      const button = this.element('button', 'g-suit-help-link g-suit-fit-guide', this.labels.fitHelp);
      button.type = 'button';
      button.setAttribute('aria-haspopup', 'dialog');
      button.addEventListener('click', () => {
        const guide = this.product.querySelector('[data-size-dialog]');
        if (guide && !guide.open) this.product.openDialog(guide, button);
      });
      return button;
    }
    buildInline(id) {
      this.inline = this.element('div', 'g-suit-inline');
      this.inlineSteps = {};
      for (const kind of ['jacket', 'pant']) {
        const step = this.element('section', 'g-suit-step');
        step.dataset.gSuitStep = kind;
        const heading = this.element('h2', 'g-suit-step-title');
        heading.id = `${id}-${kind}-step`;
        step.setAttribute('aria-labelledby', heading.id);
        const name = this.element('span', 'g-suit-step-name', this.labels[kind]);
        const summary = this.element('span', 'g-suit-step-summary');
        heading.append(name, summary);
        const slot = this.element('div', 'g-suit-step-fields');
        step.append(heading);
        if (kind === 'jacket') {
          step.append(this.fitGuideButton(), slot);
        } else {
          const note = this.element('p', 'g-suit-fit-note');
          const gate = this.element('p', 'g-suit-gate', this.labels.chooseJacketInline || this.labels.chooseJacketFirst);
          const help = this.element('p', 'g-suit-step-help', this.labels.pantHelp);
          step.append(note, help, gate, slot);
          this.inlineFitNote = note;
          this.inlineGate = gate;
        }
        this.inline.append(step);
        this.inlineSteps[kind] = {step, summary, slot};
      }
    }
    layout() {
      if (!this.enhanced && !this.inline) return;
      const mode = this.desktopQuery.matches ? 'inline' : 'drawer';
      if (this.mode === mode) return;
      for (const {dialog} of Object.values(this.dialogs)) {
        if (dialog.open) { dialog.gSuitSnapshot = null; dialog.gSuitCommitted = true; dialog.close(); }
      }
      if (mode === 'inline') {
        this.inlineSteps.jacket.slot.append(this.product.appOptions, this.jacket);
        this.inlineSteps.pant.slot.append(this.pant);
      } else {
        this.dialogs.jacket.body.append(this.product.appOptions, this.jacket);
        this.dialogs.pant.body.append(this.pant);
      }
      this.controls.hidden = mode === 'inline';
      this.inline.hidden = mode !== 'inline';
      this.mode = mode;
      this.product.dataset.gSuitLayout = mode;
      if (this.enhanced) this.refresh();
    }
    // Text for the main button while sizes are missing: the next step to take.
    nextStepLabel() {
      if (!this.enhanced) return '';
      if (!this.ready('jacket')) return this.labels.selectJacketStep || '';
      if (!this.ready('pant')) return this.labels.selectPantStep || '';
      return '';
    }
    ready(kind) {
      const jacket = Boolean(this.selected(this.jacket) && this.property('Fit') && this.property('Jacket Length'));
      return kind === 'jacket' ? jacket : jacket && pantChoices(this.selected(this.jacket), this.pairs).some(choice => choice.waist === this.selected(this.pant) && choice.compatible);
    }
    nativePairReady() { return this.ready('pant'); }
    requiresConfirmation() { return false; }
    fulfillmentPlan() { return fulfillmentPlan(this.selected(this.jacket), this.selected(this.pant), this.pairs); }
    customOrder() { return false; }
    complete() { return !this.enhanced || this.ready('pant') && Boolean(this.fulfillmentPlan()); }
    requestInputs() {
      const plan = this.fulfillmentPlan();
      if (!plan) return null;
      return this.product.optionGroups.map(group => group.querySelector('input:checked'));
    }
    matchesFulfillment(variant) {
      const plan = this.fulfillmentPlan();
      const options = variant?.options;
      return Boolean(plan && Array.isArray(options) && options[this.product.optionGroups.indexOf(this.jacket)] === plan.chest && options[this.product.optionGroups.indexOf(this.pant)] === plan.referenceWaist);
    }
    syncProperties() {
      // Earlier versions added custom-pairing line-item properties; make sure none are submitted.
      if (!this.form?.querySelectorAll) return;
      this.form.querySelectorAll('[data-g-custom-pants-property]').forEach(field => { field.value = ''; field.disabled = true; });
    }
    clearUnmatchedVariant() {}
    restoreUrlWaist() {}
    snapshot() {
      return {
        jacket: this.selected(this.jacket), pant: this.selected(this.pant), explicitPant: this.explicitPant,
        properties: ['Fit', 'Jacket Length'].map(name => ({name, value: this.property(name)})),
        variant: new URL(this.window.location.href).searchParams.get('variant'),
        options: new URL(this.window.location.href).searchParams.get('option_values')
      };
    }
    restore(snapshot) {
      this.restoring = true;
      this.variantChangePending = true;
      for (const [group, value] of [[this.jacket, snapshot.jacket], [this.pant, snapshot.pant]]) group.querySelectorAll('input').forEach(input => { input.checked = input.value === value; });
      for (const property of snapshot.properties) {
        const fields = this.fields(property.name);
        fields.forEach(field => {
          if (field.type === 'radio' || field.type === 'checkbox') field.checked = field.value === property.value;
          else field.value = property.value;
        });
        (fields.find(field => field.checked) || fields[0])?.dispatchEvent(new Event('change', {bubbles: true}));
      }
      this.explicitPant = snapshot.explicitPant;
      const url = new URL(this.window.location.href);
      for (const [key, value] of [['variant', snapshot.variant], ['option_values', snapshot.options]]) {
        if (value === null) url.searchParams.delete(key);
        else url.searchParams.set(key, value);
      }
      url.searchParams.delete('g_pant_waist');
      this.window.history.replaceState({}, '', url);
      this.restoring = false;
      this.product.clearAppValidation();
      this.announce('');
      this.refresh();
      return Promise.resolve(this.product.resolveVariant(false)).catch(error => {
        if (error?.name === 'AbortError') return;
        this.product.state.variant = null;
        this.product.variantInput.value = '';
        this.product.variantError = true;
        this.product.resolving = false;
        this.product.querySelector('[data-variant-status]').textContent = this.product.labels.selectionError;
        this.product.showError(this.product.labels.selectionError, false);
        this.product.updateControls();
      });
    }
    open(kind, opener) {
      if (!this.enhanced) return;
      if (this.mode === 'inline') {
        const target = kind === 'pant' && !this.ready('jacket') ? 'jacket' : kind;
        this.inlineSteps[target].step.scrollIntoView({block: 'center', behavior: 'smooth'});
        return;
      }
      const needsJacket = kind === 'pant' && !this.ready('jacket');
      if (needsJacket) {
        kind = 'jacket';
      }
      const {dialog, guidance} = this.dialogs[kind];
      if (dialog.open) return;
      this.setText(guidance, needsJacket ? this.labels.chooseJacketFirst : this.status.textContent);
      dialog.gSuitCommitted = false;
      dialog.gSuitSnapshot = this.snapshot();
      dialog.gSuitOpener = opener;
      this.buttons[kind].button.setAttribute('aria-expanded', 'true');
      dialog.showModal();
      this.refresh();
      dialog.querySelector('.g-suit-close').focus();
      this.product.updateSticky();
    }
    close(dialog, commit) {
      if (commit && !this.ready(dialog.dataset.gSuitKind)) return;
      dialog.gSuitCommitted = commit;
      dialog.close();
    }
    reveal(field) {
      if (!this.enhanced) return false;
      const kind = this.pant.contains(field) ? 'pant' : this.jacket.contains(field) || this.product.appOptions.contains(field) ? 'jacket' : '';
      if (!kind) return false;
      if (this.mode === 'inline') {
        (field.closest('fieldset,[role="group"]') || this.inlineSteps[kind].step).scrollIntoView({block: 'center', behavior: 'instant'});
        return true;
      }
      this.open(kind, this.buttons[kind].button);
      return true;
    }
    historyChanged() {
      if (!this.enhanced) return;
      this.variantChangePending = true;
      for (const {dialog} of Object.values(this.dialogs)) if (dialog.open) { dialog.gSuitSnapshot = null; dialog.gSuitCommitted = true; dialog.close(); }
      this.announce('');
      this.explicitPant = true;
    }
    announce(message) {
      this.setText(this.status, message);
      for (const {dialog, guidance} of Object.values(this.dialogs || {})) {
        if (guidance) this.setText(guidance, dialog.open ? message : '');
      }
    }
    markVariantChange(event) {
      if (!this.enhanced || this.restoring) return;
      if ((this.jacket.contains(event.target) || this.pant.contains(event.target)) && event.target.matches('[data-option-value-id]')) this.variantChangePending = true;
    }
    availabilityText() {
      if (this.variantChangePending) {
        if (this.product.resolving) this.variantChangePending = false;
        else if (!this.product.variantError) return '';
      }
      if (this.status.textContent && !this.product.variantError && (this.product.resolving || !this.ready('pant'))) return '';
      return this.product.querySelector('[data-variant-status]')?.textContent || '';
    }
    change(event) {
      if (!this.enhanced || this.restoring) return;
      this.markVariantChange(event);
      if (this.jacket.contains(event.target) && event.target.matches('[data-option-value-id]')) {
        const plan = planPant({chest: this.selected(this.jacket), waist: this.selected(this.pant), explicit: this.explicitPant, pairs: this.pairs});
        this.pant.querySelectorAll('input').forEach(input => { input.checked = input.value === plan.waist; });
        if (plan.changed) this.announce(plan.waist ? this.labels.waistSuggested.replace('__WAIST__', plan.waist) : this.labels.waistCleared);
      }
      if (this.pant.contains(event.target) && event.target.matches('[data-option-value-id]')) {
        this.explicitPant = true;
        this.announce('');
      }
      this.refresh();
    }
    refresh() {
      if (!this.enhanced) return;
      // The main and sticky buttons read labels.choose while sizes are missing; point it at the next step.
      // refresh() runs (capture phase) before the product recomputes its button text.
      if (this.baseChooseLabel === undefined) this.baseChooseLabel = this.product.labels.choose;
      this.product.labels.choose = this.nextStepLabel() || this.baseChooseLabel;
      const chest = this.selected(this.jacket), waist = this.selected(this.pant), choices = pantChoices(chest, this.pairs);
      const fit = this.property('Fit');
      const jacketText = this.ready('jacket') ? [chest, this.property('Jacket Length'), fit].join(' · ') : this.labels.openJacket;
      const pantText = waist ? [this.labels.pantSummary.replace('__WAIST__', waist), fit ? `${fit} fit` : ''].filter(Boolean).join(' · ') : this.labels.openPant;
      this.setText(this.buttons.jacket.summary, jacketText);
      this.setText(this.buttons.pant.summary, pantText);
      // The pants follow the fit chosen for the jacket, so the shopper never picks it twice.
      const fitNote = fit && this.labels.pantFitNote ? this.labels.pantFitNote.replace('__FIT__', fit) : '';
      if (this.drawerFitNote) this.setText(this.drawerFitNote, fitNote);
      if (this.inlineSteps) {
        this.setText(this.inlineSteps.jacket.summary, this.ready('jacket') ? jacketText : '');
        this.setText(this.inlineSteps.pant.summary, waist ? pantText : '');
        this.setText(this.inlineFitNote, fitNote);
        const gated = this.mode === 'inline' && !chest;
        this.inlineGate.hidden = !gated;
        this.pant.hidden = gated;
      }
      // One badge only: "Standard" on the 6-inch-drop waist, which is also pre-selected. Other waists show just the number.
      const standardText = this.labels.standardShort || 'Standard';
      for (const input of this.pant.querySelectorAll('input')) {
        const choice = choices.find(choice => choice.waist === input.value);
        const label = input.closest('label');
        // Show only the waists that are real pairings for the chosen jacket.
        const offered = !chest || Boolean(choice?.compatible);
        label.hidden = !offered;
        input.disabled = !offered;
        label.classList.toggle('is-recommended', false);
        label.classList.toggle('is-standard', offered && Boolean(choice?.standard));
        label.classList.toggle('is-incompatible', false);
        label.classList.toggle('is-confirmation-needed', false);
        label.querySelectorAll('[data-g-suit-compatibility],[data-g-suit-recommended]').forEach(badge => badge.remove());
        const visible = offered && Boolean(choice?.standard);
        let badge = label.querySelector('[data-g-suit-standard]');
        if (visible && !badge) {
          badge = this.element('small', 'g-suit-standard', standardText);
          badge.setAttribute('data-g-suit-standard', 'true');
          label.querySelector('span').append(badge);
        }
        if (badge) {this.setText(badge, standardText); badge.hidden = !visible;}
      }
      const status = this.availabilityText();
      this.syncProperties();
      if (this.status.textContent === this.labels.pairNeedsConfirmation) this.setText(this.status, '');
      for (const [kind, {dialog, confirm, availability, guidance}] of Object.entries(this.dialogs)) {
        if (guidance && guidance.textContent === this.labels.pairNeedsConfirmation) this.setText(guidance, '');
        confirm.disabled = !this.ready(kind);
        this.setText(availability, dialog.open ? status : '');
      }
    }
    destroy() {
      this.observer?.disconnect();
      this.desktopQuery?.removeEventListener('change', this.onLayoutChange);
      this.product.removeEventListener('input', this.onInputCapture, true);
      this.product.removeEventListener('change', this.onCapture, true);
    }
  }
  function attach(product) {
    const config = product.state.suitSelector;
    if (!config?.enabled || !product.appOptions || !eligible(product.state.productType, product.optionGroups.map(group => group.dataset.optionName), config.pairs)) return null;
    return new SuitSelector(product);
  }
  return {attach, SuitSelector, eligible, pantChoices, planPant, fulfillmentPlan};
});
